(ns ycweb.app.pos.voucher.voucher
  (:require
   [rum.core :as rum]
   [ycweb.components.flow :as flow]
   [ycweb.components.grid :as grid]
   [ycweb.util.core :as u]
   [ycweb.components.forms :as forms]
   [ycweb.components.wrapper :as w]
   [ycweb.components.transitions :as t]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.date :as ud]))

(def voucher-steps
  (forms/setup-steps
    [{:key :code :title "Voucher code"}
     {:key :details :title "Customer details"}
     {:key :done :title "Done"}]))

(defmethod comms/receive :voucher/lookup [app-atom e]
  (toaster/close-subtle-progress app-atom)
  (when (= (get-in e [:feedback :status]) :success)
    (grid/close-modal app-atom))
  (swap! app-atom assoc-in [:voucher :feedback] (:feedback e)))

(defn vouchers-lookup [app-atom]
  (swap! app-atom update :voucher dissoc :feedback)
  (toaster/open-subtle-progress app-atom)
  (comms/send :voucher/lookup
    {:lookup (get-in @app-atom [:voucher :code])}))

;; ================== Voucher Modal ===========================
(rum/defc voucher-modal-body < rum/reactive [app-atom config-atom]
  [:div
   [:h3
    {:class (u/bem :heading [:medium-large])}
    "Capture manual voucher"]
   (forms/input "Voucher Code:" ""
     (rum/cursor-in app-atom [:voucher :code])
     {:type :text
      :auto-focus true})])

(rum/defc voucher-modal-footer [app-atom _]
  (grid/button-row
    {:left-text "Cancel"
     :left-no-icon true
     :left-fn #(grid/dispatch-modal-action app-atom :voucher/close-modal)
     :right-text "Lookup Voucher"
     :right-style :call-to-action
     :right-no-icon true
     :right-fn #(vouchers-lookup app-atom)}))

(defmethod grid/modal-config :voucher/lookup-modal [_ _ _]
  {:body voucher-modal-body
   :footer voucher-modal-footer
   :modal-type :alert})

(defmethod grid/action :voucher/close-modal [app-atom config _]
  (let [voucher @(rum/cursor app-atom :voucher)]
    (when (and (= (count (get voucher :code)) 0) (get voucher :feedback))
      (swap! app-atom update :voucher dissoc :feedback))))

(defmethod grid/action :voucher/open-voucher-modal [app-atom config _]
  (grid/open-modal app-atom :voucher/lookup-modal {}))

(defmethod grid/action :voucher/lookup-code [app-atom {:keys [scan]} _]
  (swap! app-atom assoc-in [:voucher :code] scan)
  (vouchers-lookup app-atom))

(defmethod flow/barcode-dispatch-screen :voucher/code [_ app-atom scan]
  (grid/dispatch-action app-atom :voucher/lookup-code {:scan scan}))

(defonce voucher-lifecycle-listener
  (cmn/lifecycle-listener :voucher/setup :setup
    (fn [app-atom]
      (cmn/debounce-in app-atom [:voucher :code]
        500
        #(when (>= (count %) 6)
           (vouchers-lookup app-atom))))))

; =============== Voucher Screen Wrapper  ===========================
(defn voucher-menu-modal [app-atom]
  (grid/menu-modal app-atom
    {:items
     [{:label "Manual Voucher"
       :action :voucher/open-voucher-modal}
      {:label "Cancel"
       :action :voucher/cancel-voucher-menu-modal
       :style
       {:full true :large true :text-link true :on-dark true}}]}))

(rum/defc voucher-code < rum/reactive [app-atom]
  (let [voucher-atom (rum/cursor app-atom :voucher)
        {:keys [type status title expired-on assigned?] :as feedback} (cmn/curact voucher-atom :feedback)
        code (cmn/curact voucher-atom :code)]
    (w/wrapper app-atom "Voucher" {:steps voucher-steps :step :code :modal-fn voucher-menu-modal}
      [[:div
        {:class (u/bem :section [:medium])}
        [:h2.u-text-center
         {:class (u/bem :heading [:standard])}
         "Scan or input Voucher code"]
        (forms/input "Voucher code:" "e.g: 123456789"
          (rum/cursor voucher-atom :code)
          {:required? true
           :hint "e.g. 123456789 or 123-456-789"
           :type :barcode
           :icon? true})]

       (when feedback
         [:div.section.section--medium
          (cond
            (and (= type :valid) code)
            (feedback/feedback :success "This voucher code is valid")

            (= type :redeemed)
            (feedback/feedback :warning "This voucher code has already been redeemed")

            (= type :campaign-expired)
            (feedback/feedback :error "Promotion ended"
              ["This promotion was only valid until "
               [:b (ud/format-date expired-on "d MMMM yyyy")] " and therefore has expired."])

            (= type :voucher-expired)
            (feedback/feedback :error "Expired code"
              ["This voucher code was only valid until " [:b (ud/format-date expired-on "d MMMM yyyy")]])

            (= type :invalid-code)
            (feedback/feedback :error "Invalid code")

            :else
            (feedback/feedback :error title))])]

      []

      (grid/pos-call-to-action
        (merge
          {:left-fn #(t/transition-prev app-atom)}
          (cond
            (and (= type :valid) (not assigned?) code)
            {:right-text "Next"
             :right-fn #(t/transition-next app-atom :voucher/details)}

            (and (= type :valid) assigned? code)
            {:right-text "Reveal Voucher"
             :right-style :call-to-action
             :right-no-icon true
             :right-fn #(t/transition-next app-atom :voucher/done)}))))))

(defmethod flow/render-screen :voucher/code [app-atom]
  (voucher-code app-atom))

(defmethod comms/receive :customer/lookup [app-atom e]
  (toaster/close-subtle-progress app-atom)
  (t/transition-next app-atom :voucher/done))

(defmethod grid/action :voucher-link/customer [app-atom _]
  (let [voucher (get @app-atom :voucher)
        store (:selected-warehouse @app-atom)]
    (toaster/open-subtle-progress app-atom)
    (comms/send :customer/lookup
      {:lookup
       (assoc (:customer voucher)
         :code (:code voucher)
         :store
         {:name (:displayname store)
          :id (:store-id store)})})))

;;TODO: remove def to use util.shapes once it has been refactored to be included in cljc files
(def email-validate
  #"(?i)^[A-Z0-9._%+!\"-]+@[A-Z0-9.-]+[.][A-Z]{2,}$")

(rum/defc voucher-details < rum/reactive [app-atom]
  (let
    [customer-atom (rum/cursor-in app-atom [:voucher :customer])
     email-value (cmn/curact customer-atom :email)
     newsletter-atom (rum/cursor customer-atom :newslettersubscribe)
     newsletter-selected (rum/react newsletter-atom)
     details-completed
     (and
       (u/presence (cmn/curact customer-atom :firstname))
       (u/presence (cmn/curact customer-atom :lastname))
       (u/presence email-value))]
    (w/wrapper app-atom "Voucher" {:steps voucher-steps :step :details}
      [[:div
        {:class (u/bem :section [:medium])}
        [:h2.u-text-center
         {:class (u/bem :heading [:standard])}
         "Customer details"]

        (forms/split-input-field
          ["First name:" "e.g. Jane" (rum/cursor customer-atom :firstname) {:type :user :auto-focus (cmn/auto-focus? app-atom)}]
          ["Last name:" "e.g. Smith" (rum/cursor customer-atom :lastname) {:type :user}])

        (forms/input "Email Address:" "e.g. jane.smith@domain.com" (rum/cursor customer-atom :email)
          {:type :email :show-email-helpers true})

        (when
          (and email-value (not (re-matches email-validate email-value)))
          [:div.form-field
           (feedback/feedback :warning "Invalid E-mail address")])

        [:div.form-field.toggle-label
         [:label.form-field.toggle-label
          [:input.toggle
           (forms/checkbox-props
             newsletter-atom
             #(swap! customer-atom assoc :promotionsubscribe @newsletter-atom)
             nil
             false
             false)]
          [:span.toggle__graphic-wrapper
           [:span.toggle__graphic]]
          [:span.toggle__text
           [:span.toggle__title "Newsletters and personalised offers"]
           [:span "Find out first about new launches and deals, plus the chance to receive occasional vouchers"]]]]

        [:div.form-field.toggle-label
         [:label.form-field.toggle-label
          [:input.toggle
           (forms/checkbox-props (rum/cursor customer-atom :promotionsubscribe) nil nil newsletter-selected false)]
          [:span.toggle__graphic-wrapper
           [:span.toggle__graphic]]
          [:span.toggle__text
           [:span.toggle__title "Personalised offers only"]
           [:span "Sign up for the chance of occasional personalised vouchers, without getting our newsletters"]]]]]]

      []

      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text "Redeem Voucher"
         :right-style :call-to-action
         :right-no-icon true
         :right-fn
         (when
           details-completed
           #(grid/dispatch-action app-atom :voucher-link/customer {}))}))))

(defmethod flow/render-screen :voucher/details [app-atom]
  (voucher-details app-atom))

(defmethod grid/action :voucher/finished [app-atom config]
  (let [voucher-atom (rum/cursor app-atom :voucher)]
    (swap! app-atom dissoc :voucher)
    (t/transition-next app-atom :main-menu)))

(rum/defc voucher-done < rum/reactive [app-atom]
  (let [{:keys [percentage amount remaining expires-on] :as feedback}
        (cmn/curact app-atom [:voucher :feedback])]
    (w/wrapper app-atom "Voucher" {:steps voucher-steps :step :done}
      [[:div {:class (u/bem :section [:medium])}
        [:h2.u-text-center
         {:class (u/bem :heading [:large])}
         (cond
           (not (zero? amount))
           [:b "Voucher is worth R" (u/number-format remaining) "!"]

           (not (zero? percentage))
           [:b "Voucher grants " percentage "% off!"])]

        [:p.u-text-center {:class (u/bem :paragraph [:flush :small])}
         (when (and (not (zero? amount)) (not= amount remaining))
           (list
             "This is the remaining amount on the voucher. The initial value was "
             [:b (str "R" (u/number-format amount))]
             ". "))
         "This voucher may be used now or saved for later."]

        [:div..u-margin-top-24
         [:i.u-block-center
          {:class (u/bem :icon [:block :enormous :emoticon-happy :3-3])}]]

        (when (u/presence expires-on)
          [:div.u-margin-top-24
           (feedback/feedback
             :warning " The voucher will expire on: "
             (ud/format-date expires-on "d MMMM yyyy") nil :warning)])]]

      []

      (grid/pos-call-to-action
        {:single-fn #(grid/dispatch-action app-atom :voucher/finished {})}))))

(defmethod flow/render-screen :voucher/done [app-atom]
  (voucher-done app-atom))